<template>
  <nav :class="['fixed top-0 w-full z-50 transition-all duration-300', scrolled ? 'bg-white shadow-md' : 'bg-primary']">
    <div class="container mx-auto px-4 md:px-6">
      <div class="flex items-center justify-between">
        <!-- Logo - with href for SEO -->
        <a href="/" @click.prevent="$router.push('/')">
          <img src="/icons/SAFARISTRIVESbgremoved.png" alt="Safari Strives" loading="eager" class="h-16 md:h-20 lg:h-24 object-cover cursor-pointer">
        </a>

        <div class="hidden md:flex items-center space-x-4 lg:space-x-8 flex-1 justify-center">
          <template v-for="item in navItems" :key="item.id">
            <!-- About Dropdown with href -->
            <div v-if="item.id === 'about'" class="relative group">
              <a :href="getItemHref(item)"
                 @click.prevent="handleNavigation(item)"
                 :class="['cursor-pointer transition-colors text-sm lg:text-base', scrolled ? 'text-safari-black hover:text-primary' : 'text-white hover:text-secondary']">
                {{ item.label }}
              </a>
              <!-- Dropdown Menu -->
              <div class="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                <div class="py-2">
                  <a v-for="subItem in aboutSubMenu" :key="subItem.id"
                     :href="subItem.route"
                     @click.prevent="handleNavigation(subItem)"
                     class="block px-4 py-2 text-sm text-safari-black hover:bg-primary hover:text-white transition-colors cursor-pointer">
                    {{ subItem.label }}
                  </a>
                </div>
              </div>
            </div>

            <!-- Regular Nav Items with href -->
            <a v-else
               :href="getItemHref(item)"
               @click.prevent="handleNavigation(item)"
               :class="['cursor-pointer transition-colors text-sm lg:text-base', scrolled ? 'text-safari-black hover:text-primary' : 'text-white hover:text-secondary']">
              {{ item.label }}
            </a>
          </template>

          <!-- Search Input -->
          <div class="relative w-48 max-w-xs ml-4">
            <div class="relative">
              <input
                v-model="searchQuery"
                @input="handleSearch"
                @focus="showSuggestions = true"
                @blur="handleSearchBlur"
                @keydown="handleKeydown"
                type="text"
                placeholder="Search pages, programs, topics..."
                :class="['w-full px-4 py-2 pl-10 rounded-lg text-sm transition-colors', scrolled ? 'bg-gray-100 text-safari-black placeholder-gray-500 border border-gray-200' : 'bg-white/20 text-white placeholder-white/70 border border-white/30']"
              >
              <svg class="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" :class="scrolled ? 'text-gray-500' : 'text-white/70'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
            </div>

            <!-- Search Suggestions Dropdown -->
            <div v-if="showSuggestions && (filteredResults.length > 0 || searchQuery.trim())"
                 class="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 overflow-y-auto z-50">
              <div v-if="filteredResults.length > 0">
                <a
                  v-for="(result, index) in filteredResults"
                  :key="result.route"
                  :href="result.route"
                  @click.prevent="selectResult(result)"
                  :class="['block px-4 py-3 hover:bg-gray-100 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0', selectedIndex === index ? 'bg-gray-50' : '']"
                >
                  <div class="font-semibold text-safari-black text-sm">{{ result.title }}</div>
                  <div class="text-xs text-gray-500 mt-1">{{ result.category }}</div>
                </a>
              </div>
              <div v-else class="px-4 py-3 text-sm text-gray-500 text-center">
                No results found for "{{ searchQuery }}"
              </div>
            </div>
          </div>
        </div>

        <!-- Get Involved Button -->
        <a href="https://docs.google.com/forms/d/e/1FAIpQLSfFWo4Kbs87MZuRUMjLMV9fLjF7lfOCwzbFJ_wenR6yuEqAPQ/viewform" target="_blank" rel="noopener noreferrer">
          <Button :variant="scrolled ? 'default' : 'outline'" class="hidden md:block bg-primary hover:bg-accent text-sm lg:text-base">Get Involved</Button>
        </a>

        <!-- Mobile Menu Toggle -->
        <button @click="mobileMenuOpen = !mobileMenuOpen" class="md:hidden" :class="scrolled ? 'text-safari-black' : 'text-white'">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
          </svg>
        </button>
      </div>
    </div>

    <!-- Mobile Menu -->
    <div v-if="mobileMenuOpen" class="md:hidden bg-white shadow-lg">
      <div class="px-4 py-4 space-y-3">
        <!-- Mobile Search -->
        <div class="relative mb-3">
          <div class="relative">
            <input
              v-model="searchQuery"
              @input="handleSearch"
              @focus="showSuggestions = true"
              @blur="handleSearchBlur"
              @keydown="handleKeydown"
              type="text"
              placeholder="Search pages, programs, topics..."
              class="w-full px-4 py-2 pl-10 rounded-lg text-sm bg-gray-100 text-safari-black placeholder-gray-500 border border-gray-200"
            >
            <svg class="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
          </div>

          <!-- Mobile Search Suggestions -->
          <div v-if="showSuggestions && (filteredResults.length > 0 || searchQuery.trim())"
               class="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 overflow-y-auto z-50">
            <div v-if="filteredResults.length > 0">
              <a
                v-for="result in filteredResults"
                :key="result.route"
                :href="result.route"
                @click.prevent="selectResult(result)"
                class="block px-4 py-3 hover:bg-gray-100 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
              >
                <div class="font-semibold text-safari-black text-sm">{{ result.title }}</div>
                <div class="text-xs text-gray-500 mt-1">{{ result.category }}</div>
              </a>
            </div>
            <div v-else class="px-4 py-3 text-sm text-gray-500 text-center">
              No results found for "{{ searchQuery }}"
            </div>
          </div>
        </div>

        <!-- Mobile Nav Items -->
        <template v-for="item in navItems" :key="item.id">
          <div v-if="item.id === 'about'" class="py-2">
            <a :href="getItemHref(item)"
               @click.prevent="handleNavigation(item); mobileMenuOpen = false"
               class="block text-safari-black hover:text-primary cursor-pointer py-2 font-semibold">
              {{ item.label }}
            </a>
            <div class="pl-4 mt-2 space-y-2">
              <a v-for="subItem in aboutSubMenu" :key="subItem.id"
                 :href="subItem.route"
                 @click.prevent="handleNavigation(subItem); mobileMenuOpen = false"
                 class="block text-safari-black hover:text-primary cursor-pointer py-1 text-sm">
                {{ subItem.label }}
              </a>
            </div>
          </div>
          <a v-else
             :href="getItemHref(item)"
             @click.prevent="handleNavigation(item); mobileMenuOpen = false"
             class="block text-safari-black hover:text-primary cursor-pointer py-2">
            {{ item.label }}
          </a>
        </template>

        <!-- Mobile Get Involved Button -->
        <a href="https://docs.google.com/forms/d/e/1FAIpQLSfFWo4Kbs87MZuRUMjLMV9fLjF7lfOCwzbFJ_wenR6yuEqAPQ/viewform" target="_blank" rel="noopener noreferrer" class="w-full">
          <Button class="w-full bg-primary hover:bg-accent">Get Involved</Button>
        </a>
      </div>
    </div>
  </nav>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { Button } from '@/components/ui/button'

interface SearchResult {
  title: string
  category: string
  route: string
  keywords: string
}

interface NavItem {
  id: string
  label: string
  type: string
  route?: string
}

@Options({
  components: { Button },
  data(): {
    scrolled: boolean
    mobileMenuOpen: boolean
    searchQuery: string
    showSuggestions: boolean
    selectedIndex: number
    searchResults: SearchResult[]
    navItems: NavItem[]
    aboutSubMenu: NavItem[]
    } {
    return {
      scrolled: false,
      mobileMenuOpen: false,
      searchQuery: '',
      showSuggestions: false,
      selectedIndex: -1,
      searchResults: [
        // Main Pages
        { title: 'Home', category: 'Page', route: '/', keywords: 'homepage main landing' },
        { title: 'About Us', category: 'Page', route: '/about', keywords: 'mission vision story history' },
        { title: 'Our Impact', category: 'Page', route: '/impact', keywords: 'results success stories films financial snapshot' },
        { title: 'Our Team', category: 'Page', route: '/team', keywords: 'leadership staff members advisors' },
        { title: 'Contact Us', category: 'Page', route: '/contact', keywords: 'get in touch volunteer partnership' },

        // Programs
        { title: 'Microloan Program', category: 'Program', route: '/programs/microloan', keywords: 'zero interest loans women entrepreneurs capital' },

        // Key Sections
        { title: 'Vision & Mission', category: 'Section', route: '/#vision-mission', keywords: 'goals purpose values' },
        { title: 'Our Work', category: 'Section', route: '/#our-work', keywords: 'programs initiatives projects' },
        { title: 'Testimonials', category: 'Section', route: '/#testimonials', keywords: 'stories feedback reviews' },

        // Key Topics
        { title: 'Women Empowerment', category: 'Topic', route: '/programs/microloan', keywords: 'female entrepreneurs economic independence' },
        { title: 'Economic Development', category: 'Topic', route: '/impact', keywords: 'financial growth community development' },
        { title: 'Rwanda Operations', category: 'Topic', route: '/about', keywords: 'kigali local community africa' },
        { title: 'Volunteer Opportunities', category: 'Topic', route: '/contact', keywords: 'get involved help support' },
        { title: 'Partnership Inquiries', category: 'Topic', route: '/contact', keywords: 'collaborate work together sponsor' },
        { title: 'Donation Information', category: 'Topic', route: '/donate', keywords: 'contribute support funding' }
      ],
      navItems: [
        { id: 'home', label: 'Home', type: 'scroll' },
        { id: 'about', label: 'About', type: 'route' },
        { id: 'impact', label: 'Impact', type: 'route' },
        { id: 'team', label: 'Team', type: 'route' },
        { id: 'donate', label: 'Donate', type: 'route' },
        { id: 'contact', label: 'Contact', type: 'scroll' }
      ],
      aboutSubMenu: [
        { id: 'microloan', label: 'Microloan Program', type: 'route', route: '/programs/microloan' }
      ] as NavItem[]
    }
  },
  computed: {
    filteredResults() {
      if (!this.searchQuery.trim()) {
        return []
      }
      const query = this.searchQuery.toLowerCase().trim()
      return (this.searchResults as SearchResult[])
        .filter((result: SearchResult) =>
          result.title.toLowerCase().includes(query) ||
          result.category.toLowerCase().includes(query) ||
          result.keywords.toLowerCase().includes(query)
        )
        .sort((a: SearchResult, b: SearchResult) => {
          const aTitle = a.title.toLowerCase().includes(query) ? 1 : 0
          const bTitle = b.title.toLowerCase().includes(query) ? 1 : 0
          if (aTitle !== bTitle) return bTitle - aTitle

          const categoryOrder: Record<string, number> = { Page: 1, Program: 2, Section: 3, Topic: 4 }
          return (categoryOrder[a.category] || 5) - (categoryOrder[b.category] || 5)
        })
        .slice(0, 6)
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
  },
  unmounted() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    handleScroll() {
      this.scrolled = window.scrollY > 100
    },

    /**
     * Get the href attribute for a nav item
     * This ensures proper SEO and fallback when JS is disabled
     */
    getItemHref(item: NavItem): string {
      if (item.route) {
        return item.route
      } else if (item.type === 'route') {
        return `/${item.id}`
      } else if (item.id === 'home') {
        return '/'
      } else if (item.id === 'contact') {
        return '/contact'
      } else if (item.id === 'donate') {
        return '/donate'
      }
      return '#' + item.id
    },

    scrollToSection(id: string) {
      const element = document.getElementById(id)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    },

    handleNavigation(item: NavItem) {
      if (item.route) {
        this.$router.push(item.route)
      } else if (item.type === 'route') {
        this.$router.push(`/${item.id}`)
      } else if (item.id === 'home') {
        this.$router.push('/')
      } else if (item.id === 'contact') {
        this.$router.push('/contact')
      } else if (item.id === 'donate') {
        this.$router.push('/donate')
      } else {
        if (this.$route.path !== '/') {
          this.$router.push('/').then(() => {
            setTimeout(() => this.scrollToSection(item.id), 100)
          })
        } else {
          this.scrollToSection(item.id)
        }
      }
    },

    handleSearch() {
      this.selectedIndex = -1
      if (this.searchQuery.trim()) {
        this.showSuggestions = true
      } else {
        this.showSuggestions = false
      }
    },

    handleKeydown(event: KeyboardEvent) {
      if (!this.showSuggestions) return
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          this.selectedIndex = Math.min(this.selectedIndex + 1, this.filteredResults.length - 1)
          break
        case 'ArrowUp':
          event.preventDefault()
          this.selectedIndex = Math.max(this.selectedIndex - 1, -1)
          break
        case 'Enter':
          event.preventDefault()
          if (this.selectedIndex >= 0 && this.filteredResults[this.selectedIndex]) {
            this.selectResult(this.filteredResults[this.selectedIndex])
          }
          break
        case 'Escape':
          this.showSuggestions = false
          this.selectedIndex = -1
          break
      }
    },

    handleSearchBlur() {
      setTimeout(() => {
        this.showSuggestions = false
      }, 200)
    },

    selectResult(result: SearchResult) {
      this.searchQuery = ''
      this.showSuggestions = false
      this.selectedIndex = -1
      this.mobileMenuOpen = false

      if (result.route.includes('#')) {
        const [path, hash] = result.route.split('#')
        if (this.$route.path !== path) {
          this.$router.push(path).then(() => {
            setTimeout(() => {
              const element = document.getElementById(hash)
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' })
              }
            }, 100)
          })
        } else {
          const element = document.getElementById(hash)
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' })
          }
        }
      } else {
        this.$router.push(result.route)
      }
    }
  }
})
export default class Navbar extends Vue {}
</script>
